import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import { Box, Chip } from "@mui/material";
import Filters from "../../../../../assets/images/filters.svg";
import Threedots from "../../../../../assets/images/PhosphorLine.svg";
import Refresh from "../../../../../assets/images/ArrowCounterClockwise.svg";
import Expand from "../../../../../assets/images/expand.svg";
import Slider from "../../../../../assets/images/sliders 1.svg";
import CustomButton from "Samarth Module/components/buttons/CustomButton";
import MultiSelectWithSearchInput from "Samarth Module/components/dropdown/MultiSelectWithInput";
import OverflowMenu from "Samarth Module/components/overflowMenu/OverflowMenu";
import MoreFilters from "Samarth Module/components/drawers/moreFilters";
import CustomiseColumns from "Samarth Module/components/drawers/CustomiseColumns";

import {
  fetchAllocationToDeliveryOverviewTable,
  fetchAllocationLossReasons,
  fetchAllocationLossvalidity,
  fetchAllocationLossCategory,
  fetchLossHeatMap,
} from "../../../../api/LossRecoveryApi";

import AllocationToDeliveryOverviewtable from "./AllocationToDeliveryOverviewTable";
import AllocationToDeliveryLossReasonsGraph from "./AllocationToDeliveryLossReasonsGraph";
import AllocationToDeliveryLossValidityGraph from "./AllocationToDeliveryLossValidityGraph";
import AllocationToDeliveryLossCategoryGraph from "./AllocationToDeliveryLossCategoryGraph";
import AllocationToDeliveryVisibilityTable from "./AllocationToDeliveryVisibilityTable";

import styles from "./allocationToDelivery.module.css";

export const Shimmer = ({ w = "100%", h = 24, br = 10 }) => (
  <div
    className={styles.shimmerBase}
    style={{
      width: w,
      height: typeof h === "number" ? `${h}px` : h,
      borderRadius: br,
    }}
  />
);

Shimmer.propTypes = {
  w: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  h: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  br: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const AllocationToDelivery = ({
  sharedSelectedFilters,
  setSharedSelectedFilters,
  sharedUOM,
  setSharedUOM,
  sharedFiltersData = [],
  setSharedFiltersData,
  sharedColumnsData = [],
  sharedFilterLoading,
  hasFetchedSharedFilters,
  setHasFetchedSharedFilters,
  fetchSharedFilters,
  isActive = true,
}) => {
  const { accounts } = useMsal();
  const activeAccount = accounts?.[0];

  const [isDrawerOpen, setIsDrawerOpen] = useState({
    filterDrawer: false,
    customiseDrawer: false,
  });

  const [filters, setFilters] = useState({
    AllocationToDeliveryFiltersData: [],
    AllocationToDeliveryColumnsData: [],
  });

  const [apiColumnsData, setApiColumnsData] = useState([]);
  const [overviewTableData, setOverviewTableData] = useState([]);
  const [paginationData, setPaginationData] = useState(null);
  const [sortingData, setSortingData] = useState({
    sortBy: "",
    sortOrder: "ASC",
    fieldsection: "",
  });

  const [visibilityTableData, setVisibilityTableData] = useState([]);
  const [lossReasonsData, setLossReasonsData] = useState([]);
  const [lossValidityData, setLossValidityData] = useState([]);
  const [lossCategoryData, setLossCategoryData] = useState([]);
  const [expandedGraph, setExpandedGraph] = useState(null);
  const [showExpanded] = useState(false);

  const [loading, setLoading] = useState({
    dashboardLoading: true,
    tableBodyLoading: false,
    graphBodyLoading: false,
    dataLoading: false,
    // Individual loading states for progressive rendering
    lossReasonsLoading: false,
    lossValidityLoading: false,
    lossCategoryLoading: false,
    lossHeatmapLoading: false,
  });

  const hasLoadedDashboardOnceRef = useRef(false);
  const hasCompletedInitialLoadRef = useRef(false);
  const abortControllerRef = useRef(new AbortController());
  const isMountedRef = useRef(true);
  const filterDebounceRef = useRef(null);
  const isFilterChangeRef = useRef(false);

  const uomOptions = ["Cases", "Tonnes", "Eaches"];

  /**
   * Step 1: load shared filters only if not already loaded
   * Skip if filter change is in progress (debouncing)
   */
  useEffect(() => {
    if (
      activeAccount?.username &&
      !hasFetchedSharedFilters &&
      sharedFiltersData.length === 0 &&
      !isFilterChangeRef.current
    ) {
      fetchSharedFilters({
        selectedMap: sharedSelectedFilters,
        uom: sharedUOM,
      });
    }
  }, [
    activeAccount?.username,
    hasFetchedSharedFilters,
    sharedFiltersData.length,
    sharedSelectedFilters,
    sharedUOM,
    fetchSharedFilters,
  ]);

  /**
   * Sync local filters from parent shared filters
   */
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      AllocationToDeliveryFiltersData: sharedFiltersData,
      AllocationToDeliveryColumnsData: apiColumnsData.length > 0 ? apiColumnsData : sharedColumnsData,
    }));
  }, [sharedFiltersData, apiColumnsData, sharedColumnsData]);

  const handleExpand = (type) => {
    setExpandedGraph(type);
  };

  const handleCloseExpanded = () => {
    setExpandedGraph(null);
  };
  const handleCustomization = () => {
    toggleCustomizeDrawer();
  }
  const toggleCustomizeDrawer = useCallback(() => {
    setIsDrawerOpen(prev => ({ ...prev, customiseDrawer: !prev.customiseDrawer }));
  }, []);
  const getFormattedData = (response) => {
    const uomMap = {
      tonnes: "T",
      cases: "C",
      eaches: "E",
    };

    const suffix = uomMap[response?.uom?.toLowerCase()] || "";

    return (response?.data || []).map((item) => ({
      name: item["Loss Reason"] || "unaccounted",
      value: item["Loss Quantity"] || 0,
      percent: item["Loss Quantity Percentage"] ?? 0,
      suffix,
    }));
  };

  const getLossValidityFormattedData = (response) => {
    const uomMap = {
      tonnes: "T",
      cases: "C",
      eaches: "E",
    };

    const symbol = uomMap[response?.uom?.toLowerCase()] || "";

    return (
      response?.data?.map((item) => ({
        name: item["Days"],
        order: item["Days Order"],
        value: item["Loss Quantity"],
        percent: item["Loss Quantity Percentage"],
        symbol,
      })).filter((item) => item.value !== null && item.percent !== null) || []
    );
  };

  const getLossCategoryFormattedData = (response) => {
    const uomMap = {
      tonnes: "T",
      cases: "C",
      eaches: "E",
    };

    const suffix = uomMap[response?.uom?.toLowerCase()] || "";

    return (response?.data || []).map((item) => ({
      name: item["Loss Category"],
      value: item["Loss Quantity"],
      percent: item["Loss Quantity Percentage"],
      suffix,
    }));
  };

  const buildSelectionMapFromFilters = useCallback((filtersArray = []) => {
    return filtersArray.reduce((acc, filter) => {
      acc[filter.label] = filter.selectedData || [];
      return acc;
    }, {});
  }, []);

  const fetchTableData = useCallback(
    async (
      selectedMap = sharedSelectedFilters,
      page = 1,
      sortBy = "",
      sortOrder = "ASC",
      uom = sharedUOM,
      rowsPerPage = 15,
      fieldsection = ""
    ) => {
      if (!activeAccount?.username) return;

      const isFilterChange = page === 1 && !sortBy;

      setLoading((prev) => ({
        ...prev,
        dataLoading: isFilterChange,
        tableBodyLoading: false,
      }));

      try {
        const payload = {
          email: activeAccount.username,
          uom,
          filters: selectedMap || {},
          type: "Allocation To Delivery View",
          pagination: {
            page,
            rowsPerPage,
          },
          sorting: {
            sortBy,
            sortOrder,
            fieldsection,
          },
        };

        const data = await fetchAllocationToDeliveryOverviewTable(
          payload,
          abortControllerRef.current.signal
        );

        // Extract defaultColumns from API response
        if (data?.defaultColumns && Array.isArray(data.defaultColumns)) {
          setApiColumnsData(data.defaultColumns);
        }

        setOverviewTableData([...(data?.Total || []), ...(data?.data || [])]);
        setPaginationData(data?.pagination || null);
        setSortingData({
          sortBy: data?.sorting?.sortBy || sortBy,
          sortOrder: data?.sorting?.sortOrder || sortOrder,
          fieldsection: data?.sorting?.fieldsection || fieldsection,
        });
      } catch (error) {
        if (error?.name !== 'AbortError') {
          console.error("Error fetching allocation to delivery overview table:", error);
        }
      } finally {
        setLoading((prev) => ({
          ...prev,
          dataLoading: false,
          tableBodyLoading: false,
        }));
      }
    },
    [activeAccount?.username, sharedSelectedFilters, sharedUOM]
  );

  const fetchAtdDashboardData = useCallback(
    async (selectedMap = sharedSelectedFilters, uom = sharedUOM) => {
      if (!activeAccount?.username || !isActive || !isMountedRef.current) return;

      // Check if this is the initial load
      const isInitialLoad = !hasCompletedInitialLoadRef.current;

      // Set individual loading states
      setLoading((prev) => ({
        ...prev,
        dashboardLoading: isInitialLoad,  // Keep true for initial load, false for filter changes
        dataLoading: true,       // Table loading
        tableBodyLoading: false,
        lossReasonsLoading: true,
        lossValidityLoading: true,
        lossCategoryLoading: true,
        lossHeatmapLoading: true,
      }));

      const payload = {
        email: activeAccount.username,
        uom,
        filters: selectedMap || {},
        type: "Allocation To Delivery View",
      };

      const signal = abortControllerRef.current?.signal;

        // Start TABLE API - show UI when it completes
        fetchTableData(
          selectedMap,
          1,
          "",
          "ASC",
          uom,
          paginationData?.rowsPerPage || 15
        )
          .then(() => {
            if (isMountedRef.current && isActive) {
              setLoading((prev) => ({
                ...prev,
                dataLoading: false,  // Table loaded - show table UI
                dashboardLoading: false,  // Initial load complete
              }));
              hasCompletedInitialLoadRef.current = true;
            }
          })
          .catch((error) => {
            if (error.name !== 'AbortError') {
              console.error("Error fetching overview table data:", error);
            }
            if (isMountedRef.current && isActive) {
              setLoading((prev) => ({
                ...prev,
                dataLoading: false,
                dashboardLoading: false,  // Clear loading state on error
              }));
              hasCompletedInitialLoadRef.current = true;
            }
          });

        // Start ALL GRAPH APIs in parallel with individual loading states
        fetchAllocationLossReasons(payload, signal)
          .then((response) => {
            if (isMountedRef.current && isActive) {
              const formatted = getFormattedData(response);
              setLossReasonsData(formatted);
            }
          })
          .catch((error) => {
            if (error.name !== 'AbortError') {
              console.error("Error fetching loss reasons:", error);
            }
          })
          .finally(() => {
            if (isMountedRef.current && isActive) {
              setLoading((prev) => ({
                ...prev,
                lossReasonsLoading: false,
              }));
            }
          });

        fetchAllocationLossvalidity(payload, signal)
          .then((response) => {
            if (isMountedRef.current && isActive) {
              const formattedLossValidity = getLossValidityFormattedData(response);
              setLossValidityData(formattedLossValidity);
            }
          })
          .catch((error) => {
            if (error.name !== 'AbortError') {
              console.error("Error fetching loss validity:", error);
            }
          })
          .finally(() => {
            if (isMountedRef.current && isActive) {
              setLoading((prev) => ({
                ...prev,
                lossValidityLoading: false,
              }));
            }
          });

        fetchAllocationLossCategory(payload, signal)
          .then((response) => {
            if (isMountedRef.current && isActive) {
              const formattedLossCategory = getLossCategoryFormattedData(response);
              setLossCategoryData(formattedLossCategory);
            }
          })
          .catch((error) => {
            if (error.name !== 'AbortError') {
              console.error("Error fetching loss category:", error);
            }
          })
          .finally(() => {
            if (isMountedRef.current && isActive) {
              setLoading((prev) => ({
                ...prev,
                lossCategoryLoading: false,
              }));
            }
          });

        fetchLossHeatMap(payload, signal)
          .then((response) => {
            if (isMountedRef.current && isActive) {
              setVisibilityTableData(response?.data || []);
            }
          })
          .catch((error) => {
            if (error.name !== 'AbortError') {
              console.error("Error fetching loss heatmap:", error);
            }
          })
          .finally(() => {
            if (isMountedRef.current && isActive) {
              setLoading((prev) => ({
                ...prev,
                lossHeatmapLoading: false,
              }));
            }
          });
    },
    [
      activeAccount?.username,
      sharedSelectedFilters,
      sharedUOM,
      paginationData?.rowsPerPage,
      fetchTableData,
      isActive,
    ]
  );

  /**
   * Step 2: load dashboard only once after account is ready
   * Only fetch if component is active
   * Skip if filter change is in progress (debouncing)
   */
  useEffect(() => {
    if (activeAccount?.username && !hasLoadedDashboardOnceRef.current && isActive && !isFilterChangeRef.current) {
      hasLoadedDashboardOnceRef.current = true;
      abortControllerRef.current = new AbortController();
      fetchAtdDashboardData(sharedSelectedFilters, sharedUOM).catch((error) => {
        if (error?.name !== 'AbortError') {
          console.error('Error fetching ATD dashboard data:', error);
        }
      });
    }
  }, [
    activeAccount?.username,
    sharedSelectedFilters,
    sharedUOM,
    fetchAtdDashboardData,
    isActive,
  ]);

  /**
   * Abort all in-flight requests when tab becomes inactive or component unmounts
   */
  useEffect(() => {
    if (!isActive) {
      // Tab is now inactive - abort all pending requests
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();
    }

    return () => {
      abortControllerRef.current?.abort();
    };
  }, [isActive]);

  /**
   * Final cleanup on component unmount
   */
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      abortControllerRef.current?.abort();
    };
  }, []);


  const toggleFilterDrawer = useCallback(() => {
    setIsDrawerOpen((prev) => ({
      ...prev,
      filterDrawer: !prev.filterDrawer,
    }));
  }, []);

  const handleFilterChange = (label, selectedOptions) => {
    let selectedData = [];
    if (Array.isArray(selectedOptions)) {
      selectedData = selectedOptions;
    } else if (selectedOptions) {
      selectedData = [selectedOptions];
    }

    const updatedFilters = filters.AllocationToDeliveryFiltersData.map((filter) =>
      filter.label === label
        ? {
          ...filter,
          selectedData,
        }
        : filter
    );

    const newSelectedMap = buildSelectionMapFromFilters(updatedFilters);

    setFilters((prev) => ({
      ...prev,
      AllocationToDeliveryFiltersData: updatedFilters,
    }));

    setSharedFiltersData(updatedFilters);
    setSharedSelectedFilters(newSelectedMap);

    // Mark that this is a filter change to prevent duplicate API calls from useEffect
    isFilterChangeRef.current = true;

    // Clear existing debounce timer
    if (filterDebounceRef.current) {
      clearTimeout(filterDebounceRef.current);
    }

    // Abort previous in-flight requests
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    // Debounce API calls - wait 500ms before triggering
    filterDebounceRef.current = setTimeout(() => {
      isFilterChangeRef.current = false;

      fetchSharedFilters({
        selectedMap: newSelectedMap,
        uom: sharedUOM,
        force: true,
      }).catch((error) => {
        if (error?.name !== 'AbortError') {
          console.error('Error fetching shared filters:', error);
        }
      });

      fetchAtdDashboardData(newSelectedMap, sharedUOM).catch((error) => {
        if (error?.name !== 'AbortError') {
          console.error('Error fetching ATD dashboard data:', error);
        }
      });
    }, 500);
  };

  const handleUOMChange = (_label, selectedOptions) => {
    const newUOM = Array.isArray(selectedOptions)
      ? selectedOptions[0]
      : selectedOptions || "Eaches";

    setSharedUOM(newUOM);
    setHasFetchedSharedFilters(false);
    hasLoadedDashboardOnceRef.current = false;

    fetchSharedFilters({
      selectedMap: sharedSelectedFilters,
      uom: newUOM,
      force: true,
    }).catch((error) => {
      if (error?.name !== 'AbortError') {
        console.error('Error fetching shared filters:', error);
      }
    });

    fetchAtdDashboardData(sharedSelectedFilters, newUOM).catch((error) => {
      if (error?.name !== 'AbortError') {
        console.error('Error fetching ATD dashboard data:', error);
      }
    });
  };

  const handlePageChange = (
    newPage,
    sortCol,
    sortDir,
    rowsPerPage,
    fieldsection = ""
  ) => {
    const sortBy = sortCol || sortingData.sortBy;
    const sortOrder = sortDir || sortingData.sortOrder;

    setSortingData({ sortBy, sortOrder, fieldsection });
    setPaginationData((prev) => ({
      ...prev,
      page: newPage,
      rowsPerPage,
    }));

    fetchTableData(
      sharedSelectedFilters,
      newPage,
      sortBy,
      sortOrder,
      sharedUOM,
      rowsPerPage,
      fieldsection
    ).catch((error) => {
      if (error?.name !== 'AbortError') {
        console.error('Error fetching table data:', error);
      }
    });
  };

  const resetFilter = () => {
    // Reset local filters state by clearing selectedData
    const resetFiltersData = (filters.AllocationToDeliveryFiltersData || []).map((filter) => ({
      ...filter,
      selectedData: [],
    }));

    setFilters((prev) => ({
      ...prev,
      AllocationToDeliveryFiltersData: resetFiltersData,
    }));

    setSharedFiltersData(resetFiltersData);
    setSharedSelectedFilters({});
    setSharedUOM("Eaches");
    setHasFetchedSharedFilters(false);
    hasLoadedDashboardOnceRef.current = false;

    fetchSharedFilters({
      selectedMap: {},
      uom: "Eaches",
      force: true,
    }).catch((error) => {
      if (error?.name !== 'AbortError') {
        console.error('Error fetching shared filters:', error);
      }
    });

    fetchAtdDashboardData({}, "Eaches").catch((error) => {
      if (error?.name !== 'AbortError') {
        console.error('Error fetching ATD dashboard data:', error);
      }
    });
  };

  const visibleFilters = useMemo(() => {
    const allFilters = filters.AllocationToDeliveryFiltersData || [];
    const favouriteFilters = allFilters.filter(
      (filter) => filter.isFavourite === true || filter.isFavorite === true
    );
    return favouriteFilters.length > 0 ? favouriteFilters : allFilters;
  }, [filters.AllocationToDeliveryFiltersData]);

  // Chips for filters NOT shown in the top row (i.e., More Filters selections)
  const buildChipOnDelete = (filter, subIndex) => () =>
    handleFilterChange(
      filter.label,
      filter.selectedData.filter((_, i) => i !== subIndex)
    );

  const filterChips = useMemo(() => {
    const allFilters = filters.AllocationToDeliveryFiltersData || [];
    const hidden = allFilters.filter((f) => !visibleFilters.includes(f));
    const chips = [];

    hidden.forEach((filter, index) => {
      const selectedValues = filter.selectedData || [];
      selectedValues.forEach((selectedValue, subIndex) => {
        chips.push({
          key: `${filter.label}-${index}-${subIndex}`,
          label: selectedValue,
          onDelete: buildChipOnDelete(filter, subIndex),
        });
      });
    });

    return chips;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.AllocationToDeliveryFiltersData, visibleFilters]);

  //  Check if we're blocking the entire screen (only if not logged in)
  const shouldBlockEntireScreen = !activeAccount?.username;

  // Check if this is initial load (dashboardLoading true AND initial load not yet completed)
  const isInitialLoad = loading.dashboardLoading && !hasCompletedInitialLoadRef.current;

  if (shouldBlockEntireScreen) {
    return null;
  }

  const isTableLoading = loading.dataLoading || loading.tableBodyLoading || loading.dashboardLoading;
  const hasNoData = overviewTableData.length === 0;

  let overviewTableContent;
  if (isTableLoading) {
    overviewTableContent = <Shimmer h={250} />;
  } else if (hasNoData) {
    overviewTableContent = (
      <div className={styles.graphCard}>
        <div className={styles.noDataContainer}>
          <span className={styles.noDataMessage}>No data available</span>
        </div>
      </div>
    );
  } else {
    overviewTableContent = (
      <div className={styles.graphCard}>
        <div className={styles.customButtonGroup}>
          <span style={{ fontWeight: "600", fontSize: "16px" }}>
            Overview Table
          </span>
          <div style={{ display: "flex" }}>
            <CustomButton icon={Slider} label="Customise" type="mm-save" onClick={handleCustomization} />
            {!showExpanded && <div className={styles.segmentedBtnTable}>
              <button
                type="button"
                className={styles.segment}
                style={{ padding: "3px", background: "white", cursor: "pointer" }}
                onClick={() => handleExpand("table")}
                title="Expand / Zoom"
              >
                <img src={Expand} alt="Expand" className={styles.icon} />
              </button>
            </div>}
          </div>
        </div>
        <AllocationToDeliveryOverviewtable
          data={overviewTableData}
          columns={filters.AllocationToDeliveryColumnsData}
          paginationData={paginationData}
          sortingData={sortingData}
          handlePageChange={handlePageChange}
          isLoading={loading.tableBodyLoading}
        />
        {expandedGraph === "table" && (
          <div className={styles.expandedOverlay}>
            <div className={styles.expandedContainer}>
              <div className={styles.expandedHeader}>
                <span>Overview Table</span>
                <button
                  className={styles.closeButton}
                  onClick={handleCloseExpanded}
                >
                  ✕
                </button>
              </div>
              <div className={styles.expandedTableWrapper}>
                {hasNoData ? (
                  <div className={styles.noDataContainer}>
                    <span className={styles.noDataMessage}>No data available</span>
                  </div>
                ) : (
                  <AllocationToDeliveryOverviewtable
                    data={overviewTableData}
                    columns={filters.AllocationToDeliveryColumnsData}
                    paginationData={paginationData}
                    sortingData={sortingData}
                    handlePageChange={handlePageChange}
                    isLoading={loading.tableBodyLoading}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
      <div className={styles.container}>
        <div className={styles.fixedHeaderSection}>
        <div className={styles.sectionContainer}>
          {isInitialLoad || visibleFilters.length === 0 ? (
            <>
              <div className={styles.filterWrapper}><Shimmer w="200px" h={40} /></div>
              <div className={styles.filterWrapper}><Shimmer w="200px" h={40} /></div>
              <div className={styles.filterWrapper}><Shimmer w="200px" h={40} /></div>
              <div className={styles.filterWrapper}><Shimmer w="150px" h={40} /></div>
            </>
          ) : (
            <>
              {visibleFilters.map((filter, index) => (
                <div key={`${filter.label}-${index}`} className={styles.filterWrapper}>
                  <MultiSelectWithSearchInput
                    label={filter.label}
                    options={filter.data || []}
                    filterValues={filter.selectedData || []}
                    isMultiSelect={filter.isMultiSelect}
                    handleFilter={handleFilterChange}
                    type="Allocation To Delivery"
                    defaultValue={filter.defaultValue || []}
                  />
                </div>
              ))}

              <div className={styles.filterWrapper}>
                <MultiSelectWithSearchInput
                  label="Units"
                  options={uomOptions}
                  filterValues={[sharedUOM]}
                  isMultiSelect={false}
                  handleFilter={handleUOMChange}
                  type="Allocation To Delivery"
                  defaultValue="Eaches"
                />
              </div>
            </>
          )}

          <div className={styles.iconContainer}>
            <button
              type="button"
              className={styles.iconButtonReset}
              onClick={resetFilter}
              aria-label="refresh filter"
            >
              <img src={Refresh} className={styles.iconStyling} alt="refresh filter" />
            </button>

            <OverflowMenu
              menuItems={[
                {
                  label: "More Filters",
                  icon: Filters,
                  onClick: toggleFilterDrawer,
                },
              ]}
              Icon={Threedots}
            />
          </div>
        </div>

        {filterChips.length > 0 && (
          <div className={styles.filtersBtnContainer}>
            <Box className={styles.chipContainer}>
              {filterChips.map((chip) => (
                <Chip key={chip.key} label={chip.label} onDelete={chip.onDelete} />
              ))}
            </Box>
          </div>
        )}
        </div>

        <div className={styles.scrollableContent}>

        {/* <div className={styles.graphGrid}>
          {loading.graphBodyLoading ? (
            <Shimmer h={200} />
          ) : (
            <AllocationToDeliveryLossReasonsGraph
              data={lossReasonsData}
              title="Loss by loss reasons"
              handleExpand={() => handleExpand("reasons")}
              showExpanded={expandedGraph === "reasons"}
            />
          )}

          {expandedGraph === "reasons" && (
            <div className={styles.expandedOverlay}>
              <div className={styles.expandedContainer}>
                <div className={styles.expandedHeader}>
                  <span>Loss by loss reasons</span>
                  <button
                    className={styles.closeButton}
                    onClick={handleCloseExpanded}
                  >
                    ✕
                  </button>
                </div>

                <div className={styles.expandedTableWrapper}>
                  <AllocationToDeliveryLossReasonsGraph data={lossReasonsData} />
                </div>
              </div>
            </div>
          )}

          {loading.graphBodyLoading ? (
            <Shimmer h={200} />
          ) : (
            <AllocationToDeliveryLossValidityGraph
              data={lossValidityData}
              title="Loss validity value (%)"
              handleExpand={() => handleExpand("validity")}
              showExpanded={expandedGraph === "validity"}
            />
          )}

          {expandedGraph === "validity" && (
            <div className={styles.expandedOverlay}>
              <div className={styles.expandedContainer}>
                <div className={styles.expandedHeader}>
                  <span>Loss validity value (%)</span>
                  <button
                    className={styles.closeButton}
                    onClick={handleCloseExpanded}
                  >
                    ✕
                  </button>
                </div>

                <div className={styles.expandedTableWrapper}>
                  <AllocationToDeliveryLossValidityGraph data={lossValidityData} />
                </div>
              </div>
            </div>
          )}

          {loading.graphBodyLoading ? (
            <Shimmer h={200} />
          ) : (
            <div className={styles.graphCard}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span
                  style={{
                    fontWeight: "600",
                    fontSize: "16px",
                    marginTop: "10px",
                  }}
                >
                  Loss category
                </span>

                <div className={styles.segmentedBtn}>
                  <button
                    type="button"
                    className={styles.segment}
                    style={{
                      padding: "3px",
                      background: "white",
                      cursor: "pointer",
                    }}
                    onClick={() => handleExpand("category")}
                    title="Expand / Zoom"
                  >
                    {Expand}
                  </button>
                </div>
              </div>

              <AllocationToDeliveryLossCategoryGraph
                data={lossCategoryData}
                title="Loss category"
              />
            </div>
          )}

          {expandedGraph === "category" && (
            <div className={styles.expandedOverlay}>
              <div className={styles.expandedContainer}>
                <div className={styles.expandedHeader}>
                  <span>Loss category (%)</span>
                  <button
                    className={styles.closeButton}
                    onClick={handleCloseExpanded}
                  >
                    ✕
                  </button>
                </div>

                <div className={styles.expandedTableWrapper}>
                  <AllocationToDeliveryLossCategoryGraph data={lossCategoryData} />
                </div>
              </div>
            </div>
          )}

          {loading.graphBodyLoading ? (
            <Shimmer h={200} />
          ) : (
            <div className={styles.graphCard}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span
                  style={{
                    fontWeight: "600",
                    fontSize: "16px",
                    marginTop: "10px",
                  }}
                >
                  Loss Heat map- BG/Channel
                </span>

                <div className={styles.segmentedBtn}>
                  <button
                    type="button"
                    className={styles.segment}
                    style={{
                      padding: "3px",
                      background: "white",
                      cursor: "pointer",
                    }}
                    onClick={() => handleExpand("heatMap")}
                    title="Expand / Zoom"
                  >
                    {Expand}
                  </button>
                </div>
              </div>

              <AllocationToDeliveryVisibilityTable data={visibilityTableData} />
            </div>
          )}

          {expandedGraph === "heatMap" && (
            <div className={styles.expandedOverlay}>
              <div className={styles.expandedContainer}>
                <div className={styles.expandedHeader}>
                  <span>Loss Heat map- BG/Channel</span>
                  <button
                    className={styles.closeButton}
                    onClick={handleCloseExpanded}
                  >
                    ✕
                  </button>
                </div>

                <div className={styles.expandedTableWrapper}>
                  <AllocationToDeliveryVisibilityTable data={visibilityTableData} />
                </div>
              </div>
            </div>
          )}
        </div> */}

        <div className={styles.graphGrid}>
          {loading.lossReasonsLoading ? (
            <Shimmer h={200} />
          ) : (
            <AllocationToDeliveryLossReasonsGraph
              data={lossReasonsData}
              title="Loss reason stamping"
              handleExpand={() => handleExpand("reasons")}
              showExpanded={expandedGraph === "reasons"}
            />
          )}
          {expandedGraph === "reasons" && (
            <div className={styles.expandedOverlay}>
              <div className={styles.expandedContainer}>
                {/* Header with Close Button */}
                <div className={styles.expandedHeader}>
                  <span>Loss reason stamping</span>
                  <button
                    className={styles.closeButton}
                    onClick={handleCloseExpanded}
                  >
                    ✕
                  </button>
                </div>

                {/* Table */}
                <div className={styles.expandedTableWrapper}>
                  <AllocationToDeliveryLossReasonsGraph
                    data={lossReasonsData}
                    showExpanded={true}
                  />
                </div>
              </div>
            </div>
          )}

          {loading.lossValidityLoading ? (
            <Shimmer h={200} />
          ) : (
            <div className={styles.graphCard}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontWeight: "600", fontSize: "16px", marginTop: "10px", marginLeft: "8px" }}>
                  Loss validity value (%)
                </span>
                {!showExpanded && <div className={styles.segmentedBtn}>
                  <button
                    type="button"
                    className={styles.segment}
                    style={{ padding: "3px", background: "white", cursor: "pointer" }}
                    onClick={() => handleExpand("validity")}
                    title="Expand / Zoom"
                  >
                    <img src={Expand} alt="Expand" className={styles.icon} />
                  </button>
                </div>}
              </div>
              <AllocationToDeliveryLossValidityGraph
                data={lossValidityData}
              />
            </div>
          )}
          {expandedGraph === "validity" && (
            <div className={styles.expandedOverlay}>
              <div className={styles.expandedContainer}>
                {/* Header with Close Button */}
                <div className={styles.expandedHeader}>
                  <span>Loss validity value (%)</span>
                  <button
                    className={styles.closeButton}
                    onClick={handleCloseExpanded}
                  >
                    ✕
                  </button>
                </div>

                {/* Table */}
                <div className={styles.expandedTableWrapper}>
                  <AllocationToDeliveryLossValidityGraph
                    data={lossValidityData}
                  />
                </div>
              </div>
            </div>
          )}

          {loading.lossCategoryLoading ? (
            <Shimmer h={200} />
          ) : (
            <div className={styles.graphCard}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontWeight: "600", fontSize: "16px", marginTop: "10px", marginLeft: "8px" }}>
                  Loss category
                </span>
                {!showExpanded && <div className={styles.segmentedBtn}>
                  <button
                    type="button"
                    className={styles.segment}
                    style={{ padding: "3px", background: "white", cursor: "pointer" }}
                    onClick={() => handleExpand("category")}
                    title="Expand / Zoom"
                  >
                    <img src={Expand} alt="Expand" className={styles.icon} />
                  </button>
                </div>}
              </div>
              <AllocationToDeliveryLossCategoryGraph
                data={lossCategoryData}
                title="Loss category" />
            </div>
          )}
          {expandedGraph === "category" && (
            <div className={styles.expandedOverlay}>
              <div className={styles.expandedContainer}>
                {/* Header with Close Button */}
                <div className={styles.expandedHeader}>
                  <span>Loss category (%)</span>
                  <button
                    className={styles.closeButton}
                    onClick={handleCloseExpanded}
                  >
                    ✕
                  </button>
                </div>

                {/* Table */}
                <div className={styles.expandedTableWrapper}>
                  <AllocationToDeliveryLossCategoryGraph
                    data={lossCategoryData}
                  />
                </div>
              </div>
            </div>
          )}

          {loading.lossHeatmapLoading ? (
            <Shimmer h={200} />
          ) : (
            <div className={styles.graphCard}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontWeight: "600", fontSize: "16px", marginTop: "10px", marginLeft: "8px" }}>
                  Loss Heat map- BG/Channel
                </span>
                {!showExpanded && <div className={styles.segmentedBtn}>
                  <button
                    type="button"
                    className={styles.segment}
                    style={{ padding: "3px", background: "white", cursor: "pointer" }}
                    onClick={() => handleExpand("heatMap")}
                    title="Expand / Zoom"
                  >
                    <img src={Expand} alt="Expand" className={styles.icon} />
                  </button>
                </div>}
              </div>
              <AllocationToDeliveryVisibilityTable
                data={visibilityTableData?.data || visibilityTableData || []} />
            </div>
          )}
          {expandedGraph === "heatMap" && (
            <div className={styles.expandedOverlay}>
              <div className={styles.expandedContainer}>
                {/* Header with Close Button */}
                <div className={styles.expandedHeader}>
                  <span>Loss Heat map- BG/Channel</span>
                  <button
                    className={styles.closeButton}
                    onClick={handleCloseExpanded}
                  >
                    ✕
                  </button>
                </div>

                {/* Table */}
                <div className={styles.expandedTableWrapper}>
                  <AllocationToDeliveryVisibilityTable
                    data={visibilityTableData?.data || visibilityTableData || []} />
                </div>
              </div>
            </div>
          )}
        </div>

        {overviewTableContent}
        </div>

        {/* Drawers and Modals - Outside scrollable content */}
        {isDrawerOpen.filterDrawer && (
          <MoreFilters
            anchor={"right"}
            filterDrawer={isDrawerOpen.filterDrawer}
            handleFilterDrawer={toggleFilterDrawer}
            rmpmFilters={filters.AllocationToDeliveryFiltersData}
            type={"Allocation To Delivery View"}
            setRmpmFilters={(updatedFilters) => {
              const newSelectedMap = buildSelectionMapFromFilters(updatedFilters);

              setFilters((prev) => ({
                ...prev,
                AllocationToDeliveryFiltersData: updatedFilters,
              }));

              setSharedFiltersData(updatedFilters);
              setSharedSelectedFilters(newSelectedMap);

              // Mark that this is a filter change to prevent duplicate API calls from useEffect
              isFilterChangeRef.current = true;

              // Clear existing debounce timer
              if (filterDebounceRef.current) {
                clearTimeout(filterDebounceRef.current);
              }

              // Abort previous in-flight requests
              abortControllerRef.current?.abort();
              abortControllerRef.current = new AbortController();

              // Debounce API calls - wait 500ms before triggering
              filterDebounceRef.current = setTimeout(() => {
                isFilterChangeRef.current = false;

                fetchSharedFilters({
                  selectedMap: newSelectedMap,
                  uom: sharedUOM,
                  force: true,
                }).catch((error) => {
                  if (error?.name !== 'AbortError') {
                    console.error('Error fetching shared filters:', error);
                  }
                });

                fetchAtdDashboardData(newSelectedMap, sharedUOM).catch((error) => {
                  if (error?.name !== 'AbortError') {
                    console.error('Error fetching ATD dashboard data:', error);
                  }
                });
              }, 500);
            }}
            username={activeAccount.username}
            limit={9}
          />
        )}
        {isDrawerOpen.customiseDrawer && (
          <CustomiseColumns
            anchor={'right'}
            columns={[...filters["AllocationToDeliveryColumnsData"]]}
            isOpen={isDrawerOpen.customiseDrawer}
            handleCustomizeDrawer={toggleCustomizeDrawer}
            columnCustomization={(customizedColumns) => {
              setFilters(prevFilters => ({
                ...prevFilters,
                AllocationToDeliveryColumnsData: customizedColumns
              }));
              // Refetch table data after column preferences are saved
              fetchTableData(
                sharedSelectedFilters,
                paginationData?.page || 1,
                sortingData.sortBy,
                sortingData.sortOrder,
                sharedUOM,
                paginationData?.rowsPerPage || 15,
                sortingData.fieldsection
              ).catch((error) => {
                if (error?.name !== 'AbortError') {
                  console.error('Error fetching table data:', error);
                }
              });
              toggleCustomizeDrawer();
            }}
            username={activeAccount.username}
            type={"Allocation To Delivery View"}
          />
        )}
      </div>
  );
};

AllocationToDelivery.propTypes = {
  sharedSelectedFilters: PropTypes.object,
  setSharedSelectedFilters: PropTypes.func,
  sharedUOM: PropTypes.string,
  setSharedUOM: PropTypes.func,
  sharedFiltersData: PropTypes.array,
  setSharedFiltersData: PropTypes.func,
  sharedColumnsData: PropTypes.array,
  sharedFilterLoading: PropTypes.bool,
  hasFetchedSharedFilters: PropTypes.bool,
  setHasFetchedSharedFilters: PropTypes.func,
  fetchSharedFilters: PropTypes.func,
  isActive: PropTypes.bool,
};

export default AllocationToDelivery;